import '../storage.ts';
import { db } from '@qcdoc/mock-runtime/helpers.ts';
import {
  DATABASE_STORAGE_KEY, getBackingStorage, reloadDatabase, resetDatabase, runWhenIdle, saveDatabase, setSaveObserver,
} from '@qcdoc/mock-runtime/database.ts';
import { SYNC_STATE_KEY, SyncReplica } from '@qcdoc/mock-runtime/sync/replica.ts';
import { HOME_PATH, PEER_PORTAL, PEER_PORTAL_URL, PORTAL } from '../../app/portal.ts';
import { PeerLink } from './peerLink.ts';
import { bumpMockDataRevision, updateMockSyncStatus } from './status.ts';

let active: { stop(): void; link?: PeerLink } | undefined;

/**
 * Keeps this portal's browser-local mock data in step with the other portal and with other tabs of the
 * same portal. Nothing leaves the browser except postMessage traffic to the configured peer origin.
 */
export function startMockSync(): void {
  if (active || typeof window === 'undefined' || !PEER_PORTAL) return;

  const replica = new SyncReplica({
    portal: PORTAL === 'vendor' ? 'vendor' : 'internal',
    store: getBackingStorage(),
    port: {
      database: db,
      persist: saveDatabase,
      exclusive: (task) => new Promise((resolve, reject) => runWhenIdle(() => {
        try { resolve(task()); } catch (error) { reject(error); }
      })),
      wipe: resetDatabase,
    },
  });
  const link: PeerLink | undefined = PEER_PORTAL_URL
    ? new PeerLink({
      portal: PORTAL === 'vendor' ? 'vendor' : 'internal',
      peerPortal: PEER_PORTAL,
      peerOrigin: PEER_PORTAL_URL,
      replica,
      onChange: () => { updateMockSyncStatus({ connected: link?.isConnected ?? false, pending: replica.pendingCount() }); },
    })
    : undefined;

  // Without a peer there is nobody to deliver changes to, so none are recorded; tabs still share storage.
  if (link) {
    setSaveObserver((changed) => {
      const items = replica.onSaved(changed);
      if (items.length) link.broadcast(replica.toMessages(items));
    }, () => {
      const message = replica.onLocalReset();
      if (message) link.broadcast([message]);
    });
  }

  const unsubscribe = replica.subscribe((event) => {
    if (event.type === 'applied') bumpMockDataRevision();
    // Another portal (or tab) reset the shared data: cached pages and sessions are stale.
    if (event.type === 'reset' && event.remote) window.location.reload();
    updateMockSyncStatus({ pending: replica.pendingCount() });
  });

  const prefix = PORTAL === 'combined' ? '' : `qcdoc.${PORTAL}.`;
  const onStorage = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) return;
    const databaseChanged = event.key === null || event.key === prefix + DATABASE_STORAGE_KEY;
    if (!databaseChanged && event.key !== prefix + SYNC_STATE_KEY) return;
    if (databaseChanged) {
      reloadDatabase();
      bumpMockDataRevision();
    }
    replica.refresh();
    link?.flushPending();
  };
  window.addEventListener('storage', onStorage);

  updateMockSyncStatus({ enabled: true, peerPortal: PEER_PORTAL, peerUrl: PEER_PORTAL_URL, pending: replica.pendingCount() });
  link?.start();
  active = {
    link,
    stop: () => {
      link?.stop();
      unsubscribe();
      setSaveObserver(undefined);
      window.removeEventListener('storage', onStorage);
      updateMockSyncStatus({ enabled: false, connected: false });
      active = undefined;
    },
  };
}

/** Brings the other portal forward, opening it from this window when it is not connected yet. */
export function openPeerPortal(): void {
  active?.link?.open(PEER_PORTAL === 'vendor' ? '/vendor' : HOME_PATH);
}

if (import.meta.hot) import.meta.hot.dispose(() => { active?.stop(); });
